import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bankingAPI, reconciliationAPI, settingsAPI } from '../../../../api';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { Card, CardContent } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { FormLabel } from '../../../../components/ui/FormLabel';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
} from '../../../../components/ui/Table';
import { AlertTriangle, ChevronLeft, Link2, Loader2, Lock, Search, Upload } from 'lucide-react';
import { useToast } from '../../../../hooks/useToast';
import { StatementLineRow } from '../../../../components/reconciliation/StatementLineRow';
import { ManualMatchModal } from '../../../../components/reconciliation/ManualMatchModal';
import { SplitAllocationModal } from '../../../../components/reconciliation/SplitAllocationModal';
import { TransferMatchModal } from '../../../../components/reconciliation/TransferMatchModal';
import { LoanRepaymentModal } from '../../../../components/reconciliation/LoanRepaymentModal';
import { LoanDrawdownModal } from '../../../../components/reconciliation/LoanDrawdownModal';
import { CreditCardSettlementModal } from '../../../../components/reconciliation/CreditCardSettlementModal';
import { DrawingsModal } from '../../../../components/reconciliation/DrawingsModal';
import { DraftMatchReviewModal } from '../../../../components/reconciliation/DraftMatchReviewModal';
import { StatementReceiptUploadModal } from '../../../../components/reconciliation/StatementReceiptUploadModal';

/**
 * Per-account reconciliation workbench (plan §8).
 *
 * URL: /tenant/banking/reconciliation/accounts/:id
 *
 * Tabs mirror the plan's "statement-line statuses" view:
 *   Queue (unmatched | suggested | partially_matched | needs_review)
 *     — the working surface
 *   Matched        — draft matches, not yet reconciled
 *   Reconciled     — fully reconciled
 *   Ignored        — explicitly set aside
 *   All            — every status
 *
 * Filters across all tabs: search, date range, amount range.
 *
 * Row actions: Match (opens ManualMatchModal), Split (opens
 * SplitAllocationModal), Ignore / Un-ignore (PATCH status).
 *
 * Bulk actions on selection: Ignore, Mark reviewed (reset ignored lines
 * back to unmatched).
 */
const TABS = [
  { id: 'queue', label: 'Queue' },
  { id: 'matched', label: 'Matched' },
  { id: 'reconciled', label: 'Reconciled' },
  { id: 'ignored', label: 'Ignored' },
  { id: 'all', label: 'All' },
];

const QUEUE_STATUSES = ['unmatched', 'suggested', 'partially_matched', 'needs_review'];

function formatMoney(n) {
  const num = Number(n ?? 0);
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
}

export default function ReconciliationAccountWorkbench() {
  const { id: bankAccountId } = useParams();
  const navigate = useNavigate();

  const [account, setAccount] = useState(null);
  const [accountError, setAccountError] = useState('');
  const [tab, setTab] = useState('queue');

  const [filters, setFilters] = useState({
    search: '',
    start_date: '',
    end_date: '',
    amount_min: '',
    amount_max: '',
  });

  const [lines, setLines] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [counts, setCounts] = useState({ queue: 0, matched: 0, reconciled: 0, ignored: 0, all: 0 });

  const [selectedIds, setSelectedIds] = useState(new Set());

  const [matchLine, setMatchLine] = useState(null);
  const [splitLine, setSplitLine] = useState(null);
  const [transferLine, setTransferLine] = useState(null);
  const [loanRepaymentLine, setLoanRepaymentLine] = useState(null);
  const [loanDrawdownLine, setLoanDrawdownLine] = useState(null);
  const [ccSettlementLine, setCcSettlementLine] = useState(null);
  const [drawingsLine, setDrawingsLine] = useState(null);
  const [reviewLine, setReviewLine] = useState(null);
  const [receiptUploadLine, setReceiptUploadLine] = useState(null);

  const [aiPurchasesEnabled, setAiPurchasesEnabled] = useState(false);

  const [autoMatchLoading, setAutoMatchLoading] = useState(false);
  const [autoMatchSummary, setAutoMatchSummary] = useState(null);

  const { success: toastSuccess, error: toastError, ToastContainer } = useToast();

  // Load account header.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setAccountError('');
        const a = await bankingAPI.getAccount(bankAccountId);
        if (cancelled) return;
        setAccount(a);
      } catch (err) {
        if (cancelled) return;
        setAccountError(err?.response?.data?.error || err.message || 'Account not found');
      }
    })();
    return () => { cancelled = true; };
  }, [bankAccountId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await settingsAPI.get();
        if (cancelled) return;
        const flags = data.settings?.feature_flags || {};
        setAiPurchasesEnabled(flags.ai_purchases_enabled === true);
      } catch (_) {
        if (!cancelled) setAiPurchasesEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const showReceiptUpload = useMemo(() => {
    if (!account) return false;
    const lc = account.ledger_class;
    const at = account.account_type;

    if (at === 'loan_account' || lc === 'equity_drawings' || at === 'proprietor_drawings') {
      return false;
    }

    const isBusinessBank =
      lc === 'asset_bank' ||
      at === 'current' ||
      at === 'current_account' ||
      at === 'savings' ||
      at === 'savings_account' ||
      at === 'petty_cash' ||
      at === 'payment_gateway';
    const isCardSide =
      at === 'credit_card' ||
      at === 'credit_card_account' ||
      (lc === 'liability' && at !== 'loan_account');

    return isBusinessBank || isCardSide;
  }, [account]);

  const loadCounts = useCallback(async () => {
    try {
      const data = await reconciliationAPI.getStatementLineCounts(bankAccountId);
      setCounts({
        queue: data.queue || 0,
        matched: data.matched || 0,
        reconciled: data.reconciled || 0,
        ignored: data.ignored || 0,
        all: data.all || 0,
      });
    } catch (_) { /* non-fatal */ }
  }, [bankAccountId]);

  useEffect(() => {
    if (bankAccountId) loadCounts();
  }, [bankAccountId, loadCounts]);

  const loadLines = useCallback(
    async (pageOffset = 0) => {
      setLoading(true);
      setError('');
      try {
        const base = {
          bank_account_id: bankAccountId,
          limit,
          offset: pageOffset,
          search: filters.search || undefined,
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          amount_min: filters.amount_min !== '' ? filters.amount_min : undefined,
          amount_max: filters.amount_max !== '' ? filters.amount_max : undefined,
        };

        let rows = [];
        let totalCount = 0;

        if (tab === 'queue') {
          const r = await reconciliationAPI.listStatementLines({
            ...base,
            statuses: QUEUE_STATUSES.join(','),
          });
          rows = r.statement_lines || [];
          totalCount = r.total || 0;
        } else if (tab === 'all') {
          const r = await reconciliationAPI.listStatementLines(base);
          rows = r.statement_lines || [];
          totalCount = r.total || 0;
        } else {
          const statusMap = { matched: 'matched', reconciled: 'reconciled', ignored: 'ignored' };
          const r = await reconciliationAPI.listStatementLines({ ...base, status: statusMap[tab] });
          rows = r.statement_lines || [];
          totalCount = r.total || 0;
        }

        setLines(rows);
        setTotal(totalCount);
        setOffset(pageOffset);
      } catch (err) {
        setError(err?.response?.data?.error || err.message || 'Failed to load statement lines');
      } finally {
        setLoading(false);
      }
    },
    [bankAccountId, tab, filters, limit]
  );

  useEffect(() => {
    if (bankAccountId) loadLines(0);
  }, [bankAccountId, tab, loadLines]);

  const onFilter = (e) => {
    e.preventDefault();
    loadLines(0);
  };

  const resetFilters = () => {
    setFilters({ search: '', start_date: '', end_date: '', amount_min: '', amount_max: '' });
    setTimeout(() => loadLines(0), 0);
  };

  const toggleSelect = (lineId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(lineId);
      else next.delete(lineId);
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(lines.map((l) => l.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const patchStatus = async (lineId, status, reason) => {
    try {
      await reconciliationAPI.updateStatementLineStatus(lineId, { status, reason });
      await Promise.all([loadLines(offset), loadCounts()]);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not change status');
    }
  };

  const bulkIgnore = async () => {
    if (selectedIds.size === 0) return;
    const reason = window.prompt('Reason for ignoring these lines?') || null;
    setLoading(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          reconciliationAPI.updateStatementLineStatus(id, { status: 'ignored', reason })
        )
      );
      setSelectedIds(new Set());
      await Promise.all([loadLines(offset), loadCounts()]);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Bulk ignore failed');
    } finally {
      setLoading(false);
    }
  };

  const bulkMarkReviewed = async () => {
    if (selectedIds.size === 0) return;
    setLoading(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          reconciliationAPI.updateStatementLineStatus(id, { status: 'unmatched' })
        )
      );
      setSelectedIds(new Set());
      await Promise.all([loadLines(offset), loadCounts()]);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Bulk reset failed');
    } finally {
      setLoading(false);
    }
  };

  const resolveDraftMatchId = useCallback(async (lineId) => {
    const data = await reconciliationAPI.getStatementLine(lineId);
    const allocs = data.allocations || [];
    const draftAlloc = allocs.find((a) => a.match_status === 'draft');
    return draftAlloc?.match_id ?? allocs[0]?.match_id ?? null;
  }, []);

  const confirmMatchForLine = async (line) => {
    if (
      !window.confirm(
        'This will post to the ledger and finalise the match. This cannot be undone by staff. Continue?'
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      const matchId = await resolveDraftMatchId(line.id);
      if (!matchId) throw new Error('No draft match found for this line');
      await reconciliationAPI.reconcileMatch(matchId);
      toastSuccess('Match confirmed', 'Statement line reconciled.');
      await Promise.all([loadLines(offset), loadCounts()]);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not confirm match';
      toastError('Confirm failed', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const rejectMatchForLine = async (line) => {
    const reason = window.prompt('Reason for rejecting this match? (required)');
    if (!reason?.trim()) return;
    if (reason.trim().length > 1000) {
      toastError('Reject failed', 'Reason must be 1000 characters or fewer.');
      return;
    }
    setLoading(true);
    try {
      const matchId = await resolveDraftMatchId(line.id);
      if (!matchId) throw new Error('No draft match found for this line');
      await reconciliationAPI.voidMatch(matchId, reason.trim());
      toastSuccess('Match rejected', 'Statement line returned to queue.');
      await Promise.all([loadLines(offset), loadCounts()]);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not reject match';
      toastError('Reject failed', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const bulkConfirmMatches = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (
      !window.confirm(
        `Confirm ${count} draft match${count === 1 ? '' : 'es'}? This will post to the ledger and finalise each match.`
      )
    ) {
      return;
    }
    setLoading(true);
    let ok = 0;
    let failed = 0;
    let firstError = '';
    try {
      for (const lineId of selectedIds) {
        try {
          const matchId = await resolveDraftMatchId(lineId);
          if (!matchId) {
            failed += 1;
            continue;
          }
          await reconciliationAPI.reconcileMatch(matchId);
          ok += 1;
        } catch (err) {
          failed += 1;
          if (!firstError) {
            firstError = err?.response?.data?.error || err.message || 'Confirmation failed';
          }
        }
      }
      setSelectedIds(new Set());
      if (ok > 0) {
        toastSuccess(
          'Bulk confirm complete',
          `Confirmed ${ok} match${ok === 1 ? '' : 'es'}.${failed > 0 ? ` ${failed} failed.` : ''}`
        );
      }
      if (failed > 0 && ok === 0) {
        toastError('Bulk confirm failed', firstError);
        setError(firstError);
      }
      await Promise.all([loadLines(offset), loadCounts()]);
    } finally {
      setLoading(false);
    }
  };

  const bulkRejectMatches = async () => {
    if (selectedIds.size === 0) return;
    const reason = window.prompt('Reason for rejecting these matches? (required)');
    if (!reason?.trim()) return;
    if (reason.trim().length > 1000) {
      toastError('Reject failed', 'Reason must be 1000 characters or fewer.');
      return;
    }
    setLoading(true);
    let ok = 0;
    let failed = 0;
    let firstError = '';
    try {
      for (const lineId of selectedIds) {
        try {
          const matchId = await resolveDraftMatchId(lineId);
          if (!matchId) {
            failed += 1;
            continue;
          }
          await reconciliationAPI.voidMatch(matchId, reason.trim());
          ok += 1;
        } catch (err) {
          failed += 1;
          if (!firstError) {
            firstError = err?.response?.data?.error || err.message || 'Rejection failed';
          }
        }
      }
      setSelectedIds(new Set());
      if (ok > 0) {
        toastSuccess(
          'Bulk reject complete',
          `Rejected ${ok} match${ok === 1 ? '' : 'es'}.${failed > 0 ? ` ${failed} failed.` : ''}`
        );
      }
      if (failed > 0 && ok === 0) {
        toastError('Bulk reject failed', firstError);
        setError(firstError);
      }
      await Promise.all([loadLines(offset), loadCounts()]);
    } finally {
      setLoading(false);
    }
  };

  const onMatched = async () => {
    setMatchLine(null);
    setSplitLine(null);
    setTransferLine(null);
    setLoanRepaymentLine(null);
    setLoanDrawdownLine(null);
    setReviewLine(null);
    setReceiptUploadLine(null);
    await Promise.all([loadLines(offset), loadCounts()]);
  };

  const runAutoMatch = async () => {
    setAutoMatchLoading(true);
    setAutoMatchSummary(null);
    try {
      const payload = { bank_account_id: bankAccountId };
      if (tab === 'queue' && selectedIds.size > 0) {
        payload.line_ids = Array.from(selectedIds);
      }
      const result = await reconciliationAPI.autoMatchBankTransactions(payload);
      const matched = result.matched ?? 0;
      const skipped = result.skipped?.length ?? 0;

      if (matched > 0 || skipped > 0) {
        setAutoMatchSummary(result);
        toastSuccess(
          'Auto-match complete',
          `Matched ${matched} statement line${matched === 1 ? '' : 's'} to ledger transactions (draft).${
            skipped > 0 ? ` ${skipped} skipped.` : ''
          }`
        );
      } else {
        toastSuccess('Auto-match complete', 'No new matches found.');
      }

      await Promise.all([loadLines(offset), loadCounts()]);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Auto-match failed';
      toastError('Auto-match failed', msg);
      setError(msg);
    } finally {
      setAutoMatchLoading(false);
    }
  };

  const ledgerLabel = useMemo(() => {
    if (!account) return '';
    const map = { asset_bank: 'Bank / cash', liability: 'Liability', equity_drawings: 'Drawings' };
    return map[account.ledger_class] || account.ledger_class || '';
  }, [account]);

  return (
    <Layout>
      <div className="mx-auto max-w-7xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">
              <button
                type="button"
                onClick={() => navigate('/tenant/banking/reconciliation')}
                className="inline-flex items-center gap-1 hover:underline"
              >
                <ChevronLeft className="h-3 w-3" /> Reconciliation
              </button>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {account?.account_name || 'Account'}
            </h1>
            {account && (
              <div className="text-xs text-gray-500">
                {account.account_type?.replace(/_/g, ' ')}
                {ledgerLabel ? ` · ${ledgerLabel}` : ''}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={runAutoMatch}
              disabled={autoMatchLoading || loading || !bankAccountId}
            >
              {autoMatchLoading ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="mr-1 h-4 w-4" />
              )}
              {autoMatchLoading ? 'Matching…' : 'Auto-match'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/tenant/banking/reconciliation/accounts/${bankAccountId}/close`)
              }
            >
              <Lock className="mr-1 h-4 w-4" /> Month-end close
            </Button>
            <Button
              size="sm"
              onClick={() =>
                navigate(`/tenant/banking/reconciliation/accounts/${bankAccountId}/import`)
              }
            >
              <Upload className="mr-1 h-4 w-4" /> Import statement
            </Button>
          </div>
        </div>

        {accountError && (
          <Card className="mb-4 border-red-400 bg-red-50 dark:bg-red-950/30">
            <CardContent className="flex gap-2 p-4 text-xs text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{accountError}</span>
            </CardContent>
          </Card>
        )}

        {autoMatchSummary && (autoMatchSummary.matched > 0 || (autoMatchSummary.skipped?.length || 0) > 0) && (
          <Card className="mb-4 border-green-300 bg-green-50 dark:bg-green-950/30">
            <CardContent className="flex items-center justify-between gap-3 p-3 text-xs text-green-800 dark:text-green-200">
              <div>
                Auto-matched{' '}
                <span className="font-semibold">{autoMatchSummary.matched}</span> statement
                line{autoMatchSummary.matched === 1 ? '' : 's'} to ledger transactions (draft).
                {(autoMatchSummary.skipped?.length || 0) > 0 && (
                  <> {autoMatchSummary.skipped.length} skipped.</>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAutoMatchSummary(null)}
              >
                Dismiss
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Summary bar */}
        {account && (
          <Card className="mb-4">
            <CardContent className="grid grid-cols-2 gap-4 p-4 md:grid-cols-5">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">Balance</div>
                <div className="text-base font-semibold">
                  {formatMoney(account.current_balance ?? account.opening_balance)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">Unmatched</div>
                <div className={counts.queue ? 'text-base font-semibold text-amber-700 dark:text-amber-400' : 'text-base font-semibold'}>
                  {counts.queue}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">Matched (draft)</div>
                <div className="text-base font-semibold">{counts.matched}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">Reconciled</div>
                <div className="text-base font-semibold text-green-700 dark:text-green-400">
                  {counts.reconciled}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">Total lines</div>
                <div className="text-base font-semibold">{counts.all}</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="mb-4 flex gap-2 border-b border-gray-200 dark:border-gray-700">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => { setTab(t.id); setSelectedIds(new Set()); }}
              className={
                'px-3 py-2 text-xs font-medium -mb-px border-b-2 ' +
                (tab === t.id
                  ? 'border-blue-600 text-blue-700 dark:text-blue-400'
                  : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100')
              }
            >
              {t.label}
              {' '}
              <span className="ml-1 text-[10px] text-gray-500">
                ({t.id === 'queue' ? counts.queue
                  : t.id === 'matched' ? counts.matched
                    : t.id === 'reconciled' ? counts.reconciled
                      : t.id === 'ignored' ? counts.ignored
                        : counts.all})
              </span>
            </button>
          ))}
        </div>

        {/* Filters */}
        <form onSubmit={onFilter} className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-6">
          <div className="md:col-span-2">
            <FormLabel>Search</FormLabel>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
              <Input
                className="pl-7"
                placeholder="Description, reference or counterparty…"
                value={filters.search}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <FormLabel>From</FormLabel>
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters((p) => ({ ...p, start_date: e.target.value }))}
            />
          </div>
          <div>
            <FormLabel>To</FormLabel>
            <Input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters((p) => ({ ...p, end_date: e.target.value }))}
            />
          </div>
          <div>
            <FormLabel>Min amount</FormLabel>
            <Input
              type="number"
              step="0.01"
              value={filters.amount_min}
              onChange={(e) => setFilters((p) => ({ ...p, amount_min: e.target.value }))}
            />
          </div>
          <div>
            <FormLabel>Max amount</FormLabel>
            <Input
              type="number"
              step="0.01"
              value={filters.amount_max}
              onChange={(e) => setFilters((p) => ({ ...p, amount_max: e.target.value }))}
            />
          </div>
          <div className="md:col-span-6 flex gap-2">
            <Button type="submit" size="sm" disabled={loading}>Apply filters</Button>
            <Button type="button" size="sm" variant="outline" onClick={resetFilters} disabled={loading}>
              Reset
            </Button>
          </div>
        </form>

        {/* Bulk actions */}
        {selectedIds.size > 0 && (
          <Card className="mb-3 border-blue-300 bg-blue-50 dark:bg-blue-950/30">
            <CardContent className="flex items-center justify-between p-3 text-xs">
              <div>
                {selectedIds.size} line{selectedIds.size === 1 ? '' : 's'} selected
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>
                  Clear selection
                </Button>
                {tab === 'matched' && (
                  <>
                    <Button size="sm" onClick={bulkConfirmMatches} disabled={loading}>
                      Confirm selected
                    </Button>
                    <Button size="sm" variant="outline" onClick={bulkRejectMatches} disabled={loading}>
                      Reject selected
                    </Button>
                  </>
                )}
                {tab !== 'ignored' && tab !== 'matched' && tab !== 'reconciled' && (
                  <Button size="sm" variant="outline" onClick={bulkIgnore} disabled={loading}>
                    Ignore selected
                  </Button>
                )}
                {tab === 'ignored' && (
                  <Button size="sm" variant="outline" onClick={bulkMarkReviewed} disabled={loading}>
                    Move back to queue
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="mb-3 border-red-400 bg-red-50 dark:bg-red-950/30">
            <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-center text-sm text-gray-500">Loading statement lines…</div>
            ) : lines.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                No statement lines match this view.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === lines.length && lines.length > 0}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        aria-label="Select all lines on this page"
                      />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Counterparty</TableHead>
                    <TableHead align="right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l) => (
                    <StatementLineRow
                      key={l.id}
                      line={l}
                      selected={selectedIds.has(l.id)}
                      onToggleSelect={toggleSelect}
                      onMatch={(line) => setMatchLine(line)}
                      onSplit={(line) => setSplitLine(line)}
                      onTransfer={(line) => setTransferLine(line)}
                      onUploadReceipt={
                        aiPurchasesEnabled &&
                        showReceiptUpload &&
                        Number(l.amount) < 0
                          ? (line) => setReceiptUploadLine(line)
                          : undefined
                      }
                      onLoanRepayment={(line) => setLoanRepaymentLine(line)}
                      onLoanDrawdown={
                        Number(l.amount) > 0 ? (line) => setLoanDrawdownLine(line) : undefined
                      }
                      onCreditCardSettlement={
                        // Settlements move money between an asset bank account
                        // (current/savings) and a liability credit-card account.
                        // Only offer the action on lines from such accounts so
                        // we don't pollute action toolbars on loan / drawings
                        // accounts where it would always 400 server-side.
                        (() => {
                          const lc = account?.ledger_class;
                          const at = account?.account_type;
                          const isBankSide =
                            lc === 'asset_bank' ||
                            at === 'current_account' ||
                            at === 'savings_account';
                          const isCardSide =
                            lc === 'liability' ||
                            at === 'credit_card' ||
                            at === 'credit_card_account';
                          return isBankSide || isCardSide
                            ? (line) => setCcSettlementLine(line)
                            : undefined;
                        })()
                      }
                      onDrawings={
                        // Drawings events originate on either an asset bank
                        // account (business-side transfer / personal spend /
                        // reimbursement) or on an equity_drawings account
                        // itself (business_spend_from_personal). Suppress the
                        // action on loan / credit card / cash accounts where
                        // it would always 400 server-side.
                        (() => {
                          const lc = account?.ledger_class;
                          const at = account?.account_type;
                          const isBusinessBank =
                            lc === 'asset_bank' ||
                            at === 'current_account' ||
                            at === 'savings_account' ||
                            at === 'petty_cash' ||
                            at === 'payment_gateway';
                          const isDrawings =
                            lc === 'equity_drawings' ||
                            at === 'proprietor_drawings';
                          return isBusinessBank || isDrawings
                            ? (line) => setDrawingsLine(line)
                            : undefined;
                        })()
                      }
                      onIgnore={(line) => {
                        const reason = window.prompt('Reason for ignoring this line?') || null;
                        patchStatus(line.id, 'ignored', reason);
                      }}
                      onUnignore={(line) => patchStatus(line.id, 'unmatched')}
                      onViewMatch={tab === 'matched' ? (line) => setReviewLine(line) : undefined}
                      onConfirmMatch={tab === 'matched' ? confirmMatchForLine : undefined}
                      onRejectMatch={tab === 'matched' ? rejectMatchForLine : undefined}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {total > limit && (
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <div>
              Showing {offset + 1}–{Math.min(offset + lines.length, total)} of {total}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={offset === 0 || loading}
                onClick={() => loadLines(Math.max(0, offset - limit))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={offset + limit >= total || loading}
                onClick={() => loadLines(offset + limit)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <ManualMatchModal
        open={!!matchLine}
        onOpenChange={(v) => { if (!v) setMatchLine(null); }}
        line={matchLine}
        bankAccountId={bankAccountId}
        onMatched={onMatched}
      />
      <SplitAllocationModal
        open={!!splitLine}
        onOpenChange={(v) => { if (!v) setSplitLine(null); }}
        line={splitLine}
        bankAccountId={bankAccountId}
        onMatched={onMatched}
      />
      <TransferMatchModal
        open={!!transferLine}
        onOpenChange={(v) => { if (!v) setTransferLine(null); }}
        line={transferLine}
        bankAccountId={bankAccountId}
        onMatched={onMatched}
      />
      <LoanRepaymentModal
        open={!!loanRepaymentLine}
        onOpenChange={(v) => { if (!v) setLoanRepaymentLine(null); }}
        line={loanRepaymentLine}
        bankAccountId={bankAccountId}
        onMatched={onMatched}
      />
      <LoanDrawdownModal
        open={!!loanDrawdownLine}
        onOpenChange={(v) => { if (!v) setLoanDrawdownLine(null); }}
        line={loanDrawdownLine}
        bankAccountId={bankAccountId}
        onMatched={onMatched}
      />
      <CreditCardSettlementModal
        open={!!ccSettlementLine}
        onOpenChange={(v) => { if (!v) setCcSettlementLine(null); }}
        line={ccSettlementLine}
        bankAccountId={bankAccountId}
        onMatched={onMatched}
      />
      <DrawingsModal
        open={!!drawingsLine}
        onOpenChange={(v) => { if (!v) setDrawingsLine(null); }}
        line={drawingsLine}
        bankAccountId={bankAccountId}
        onMatched={onMatched}
      />
      <DraftMatchReviewModal
        open={!!reviewLine}
        onOpenChange={(v) => { if (!v) setReviewLine(null); }}
        line={reviewLine}
        bankAccountId={bankAccountId}
        onUpdated={onMatched}
      />
      <StatementReceiptUploadModal
        open={!!receiptUploadLine}
        onOpenChange={(v) => { if (!v) setReceiptUploadLine(null); }}
        line={receiptUploadLine}
        bankAccountId={bankAccountId}
        onComplete={onMatched}
      />
      <ToastContainer />
    </Layout>
  );
}
